<template>
  <div class="relative">
    <div class="w-full grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-1">
      <NuxtLink
        v-for="(unit, idx) in visibleUnits"
        :key="unit.unit_id || unit.id"
        :to="{
          path: `/table/unit_view/${unit.id}`,
          query: {
            edit: 'true',
          },
        }"
        class="border rounded p-4 shadow bg-white"
      >
        <h3 class="font-bold mb-2">{{ unit.unit_reference }}</h3>
        <div class="text-sm space-y-2">
          <p v-if="unit.unit_type_description">
            <span class="font-medium">Status:</span>
            {{ unit.unit_status_description }}
          </p>
          <p v-if="unit.unit_type_description">
            <span class="font-medium">Category:</span>
            {{ unit.unit_category_description }}
          </p>
          <p v-if="unit.StandNumber">
            <span class="font-medium">Stand Number:</span>
            {{ unit.stand_number }}
          </p>
          <p v-if="unit.complex_description">
            <span class="font-medium">Complex:</span>
            {{ unit.complex_description }}
          </p>
          <p v-if="unit.rekno">
            <span class="font-medium">Reference:</span> {{ unit.rekno }}
          </p>
          <p v-if="unit.assigned_app_user_id">
            <span class="font-medium">Assigned to:</span>
            {{ unit.app_user_display_name }}
          </p>
          <p v-if="unit.Comments">
            <span class="font-medium">Comments:</span> {{ unit.comments }}
          </p>
        </div>
      </NuxtLink>
      <!-- Fade effect overlay -->
      <div
        v-if="showShowMore && !showAll"
        class="absolute bottom-12 left-0 w-full h-24 pointer-events-none bg-gradient-to-t from-white to-transparent z-10 transition-opacity duration-300"
      ></div>
    </div>
    <div class="flex justify-center mt-2" v-if="showShowMore">
      <button
        class="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition-colors z-20 relative"
        @click="toggleShowAll"
      >
        {{ showAll ? "Show Less" : "Show More" }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
const value = defineModel<any>("value");
const form = defineModel<any>("form", {
  default: () => ({}),
});
const props = defineProps<{
  data: unknown;
}>();

// The query-set response is keyed by meta.query_set.description, which for this panel reads
// "Onwer Unit Query Set" (typo in the metadata), not "OwnerUnits". Group 6 holds a single set,
// so fall back to the first array in the response rather than depending on that string.
const ownerUnits = computed(() => {
  const data = props.data;
  if (!data) return [];
  if (Array.isArray(data.OwnerUnits)) return data.OwnerUnits;
  const firstSet = Object.values(data).find((set) => Array.isArray(set));
  return (firstSet as any[]) || [];
});

const showAll = ref(false);
const screen = ref<"sm" | "md" | "lg">("lg");

function updateScreen() {
  const w = window.innerWidth;
  if (w < 768) screen.value = "sm";
  else if (w < 1024) screen.value = "md";
  else screen.value = "lg";
}

onMounted(() => {
  updateScreen();
  window.addEventListener("resize", updateScreen);
});
onUnmounted(() => {
  window.removeEventListener("resize", updateScreen);
});

const limits = { sm: 1, md: 4, lg: 5 };

const visibleUnits = computed(() => {
  if (showAll.value) return ownerUnits.value;
  const limit = limits[screen.value];
  return ownerUnits.value?.slice(0, limit) || [];
});

const showShowMore = computed(() => {
  if (!ownerUnits.value) return false;
  const limit = limits[screen.value];
  return ownerUnits.value.length > limit;
});

function toggleShowAll() {
  showAll.value = !showAll.value;
}
</script>

<style scoped></style>

